import { makeChoices } from "../../distractors";
import { text } from "../../format";
import type { QuestionDraft, Rng, TopicDef } from "../../types";
import { both, numeric, q } from "../helpers";

function tier1(rng: Rng): QuestionDraft {
  const x = rng.int(1, 50);
  const a = rng.int(1, 30);
  const variant = rng.pick(["x+a", "x-a", "a+x"] as const);
  const b = variant === "x-a" ? x - a : x + a;
  const eq =
    variant === "x+a" ? `x + ${a} = ${b}` : variant === "a+x" ? `${a} + x = ${b}` : `x − ${a} = ${b}`;
  const wrong = variant === "x-a" ? b - a : b + a;
  const choice4 = makeChoices(rng, x, [wrong, x + 1, x - 1, Math.abs(b - a) + 1], (r) => r.int(1, 80));
  const step = variant === "x-a" ? `x = ${b} + ${a}` : `x = ${b} − ${a}`;
  return q(
    both(`Rozwiąż równanie: ${eq}`, `Solve the equation: ${eq}`),
    numeric(x),
    [`Przenosimy liczbę na drugą stronę ze zmienionym znakiem: ${step}.`, `x = ${x}.`],
    [`Move the number to the other side with the opposite sign: ${step}.`, `x = ${x}.`],
    { choice4 },
  );
}

function tier2(rng: Rng): QuestionDraft {
  const x = rng.int(2, 15);
  const a = rng.int(2, 12);
  const useDiv = rng.chance(0.4);
  if (useDiv) {
    const b = x;
    const value = x * a;
    const choice4 = makeChoices(rng, value, [x + a, x - a, x * a + a, x * a * 2], (r) => r.int(1, 200));
    return q(
      both(`Rozwiąż równanie: x : ${a} = ${b}`, `Solve the equation: x ÷ ${a} = ${b}`),
      numeric(value),
      [`Mnożymy obie strony przez ${a}: x = ${b} · ${a}.`, `x = ${value}.`],
      [`Multiply both sides by ${a}: x = ${b} × ${a}.`, `x = ${value}.`],
      { choice4 },
    );
  }
  const b = a * x;
  const choice4 = makeChoices(rng, x, [b - a, b + a, b * a, x + 1], (r) => r.int(1, 60));
  return q(
    both(`Rozwiąż równanie: ${a}x = ${b}`, `Solve the equation: ${a}x = ${b}`),
    numeric(x),
    [`Dzielimy obie strony przez ${a}: x = ${b} : ${a}.`, `x = ${x}.`],
    [`Divide both sides by ${a}: x = ${b} ÷ ${a}.`, `x = ${x}.`],
    { choice4 },
  );
}

function tier3(rng: Rng): QuestionDraft {
  const x = rng.int(-6, 12);
  const a = rng.int(2, 9);
  const b = rng.int(1, 20);
  const plus = rng.chance(0.5);
  const c = plus ? a * x + b : a * x - b;
  const eq = plus ? `${a}x + ${b} = ${c}` : `${a}x − ${b} = ${c}`;
  const step1 = plus ? `${a}x = ${c} − ${b} = ${c - b}` : `${a}x = ${c} + ${b} = ${c + b}`;
  const wrongSign = plus ? (c + b) / a : (c - b) / a;
  const choice4 = makeChoices(rng, x, [wrongSign, -x, x + 1, x - 1], (r) => r.int(-10, 20));
  return q(
    both(`Rozwiąż równanie: ${eq}`, `Solve the equation: ${eq}`),
    numeric(x),
    [`Przenosimy ${b} na prawą stronę: ${step1}.`, `Dzielimy przez ${a}: x = ${x}.`],
    [`Move ${b} to the right side: ${step1}.`, `Divide by ${a}: x = ${x}.`],
    { choice4 },
  );
}

export const equationsSimple: TopicDef = {
  id: "sp6.equations_simple",
  level: "sp6",
  name: text("Równania", "Equations"),
  modes: ["numeric", "choice4"],
  generate: (rng, tier) => (tier === 1 ? tier1(rng) : tier === 2 ? tier2(rng) : tier3(rng)),
};
